
import { Investor } from "@/types/investor";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface InvestorInfoProps {
  investor: Investor;
  selectedContactData: any;
  isCustomContact: boolean;
}

const InvestorInfo = ({ investor, selectedContactData, isCustomContact }: InvestorInfoProps) => {
  // Check if an investor has been processed - primarily using enrichedWith
  const isInvestorProcessed = (investor: Investor): boolean => {
    // Primary indicator: enrichedWith field
    if (investor.enrichedWith) {
      return true;
    }
    
    // Secondary indicators if enrichedWith is missing
    return (
      !!investor.lastRelevanceUpdate ||
      !!investor.investmentFocus ||
      (investor.portfolioCompanies && investor.portfolioCompanies.length > 0) ||
      (investor.keywords && investor.keywords.length > 0)
    );
  };
  
  const processed = isInvestorProcessed(investor);

  return (
    <Card className="bg-gray-50">
      <CardContent className="pt-6 space-y-2">
        <div>
          <p className="text-sm font-medium">Firm:</p>
          <div className="flex items-center">
            <p>{investor.firm}</p>
            {investor.website && (
              <a 
                href={investor.website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-2 text-blue-600 hover:text-blue-800 inline-flex items-center"
              >
                <ExternalLink size={14} className="ml-1" />
                <span className="sr-only">Visit website</span>
              </a>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div>
            <p className="text-sm font-medium">Relevance Score:</p>
            {processed ? (
              <p>{investor.relevanceScore.toFixed(1)}/10</p>
            ) : (
              <Badge variant="outline" className="bg-gray-100 text-gray-500">
                0.0
              </Badge>
            )}
          </div>
          {investor.confidence && processed && (
            <div>
              <p className="text-sm font-medium">Data Confidence:</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Badge 
                      variant={
                        investor.confidence === 'high' ? "success" : 
                        investor.confidence === 'medium' ? "warning" : "destructive"
                      }
                    >
                      {investor.confidence}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-sm">
                      {investor.confidence === 'high' 
                        ? 'Information verified from multiple reliable sources' 
                        : investor.confidence === 'medium'
                        ? 'Information found but not fully verified'
                        : 'Limited verifiable information found'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
          {investor.enrichedWith && processed && (
            <div>
              <p className="text-sm font-medium">Enriched with:</p>
              <Badge variant="outline" className={
                investor.enrichedWith === 'perplexity' 
                  ? "bg-purple-100 text-purple-800 border-purple-300" 
                  : "bg-green-100 text-green-800 border-green-300"
              }>
                {investor.enrichedWith === 'perplexity' ? 'Perplexity' : 'OpenAI'}
              </Badge>
            </div>
          )}
        </div>
        <div>
          <p className="text-sm font-medium">Investment Focus:</p>
          <p>{investor.investmentFocus || "Not available"}</p>
        </div>
        {investor.portfolioCompanies && investor.portfolioCompanies.length > 0 && (
          <div>
            <p className="text-sm font-medium">Portfolio Companies:</p>
            <div className="flex flex-wrap gap-1">
              {investor.portfolioCompanies.map((company, idx) => {
                // Check if the company has a URL format (added during enrichment)
                const parts = company.split('|');
                const companyName = parts[0].trim();
                const companyUrl = parts.length > 1 ? parts[1].trim() : null;
                
                return companyUrl ? (
                  <a 
                    key={idx}
                    href={companyUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 hover:text-blue-800 inline-flex items-center"
                  >
                    {companyName}
                    <ExternalLink size={12} className="ml-1" />
                    {idx < investor.portfolioCompanies.length - 1 && ", "}
                  </a>
                ) : (
                  <span key={idx}>
                    {companyName}
                    {idx < investor.portfolioCompanies.length - 1 && ", "}
                  </span>
                );
              })}
            </div>
          </div>
        )}
        {investor.keywords && investor.keywords.length > 0 && (
          <div>
            <p className="text-sm font-medium">Keywords:</p>
            <p>{investor.keywords.join(", ")}</p>
          </div>
        )}
        {investor.notes && (
          <div>
            <p className="text-sm font-medium">Notes:</p>
            <p className="text-xs text-gray-600">{investor.notes}</p>
          </div>
        )}
        {investor.sources && investor.sources.length > 0 && (
          <div>
            <p className="text-sm font-medium flex items-center gap-1">
              Sources: <Info size={14} className="text-gray-500" />
            </p>
            <div className="max-h-24 overflow-y-auto text-xs space-y-1">
              {investor.sources.map((source, index) => (
                <div key={index} className="flex items-start">
                  <span className="text-gray-500 mr-1">{index + 1}.</span>
                  <a 
                    href={source} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 hover:text-blue-800 break-all inline-flex items-center"
                  >
                    {source.length > 60 ? source.substring(0, 60) + '...' : source}
                    <ExternalLink size={10} className="ml-1 inline-block" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
        {selectedContactData && (
          <div>
            <p className="text-sm font-medium">Selected Contact:</p>
            <p>
              {selectedContactData.name} 
              {isCustomContact 
                ? ` - ${selectedContactData.email}`
                : ` - ${selectedContactData.role || "No role"}`
              }
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InvestorInfo;
